/**
 * HowToRead — a collapsed "?" / "How to read" affordance for a single computed metric.
 *
 * Default is closed so a dense analytics surface stays clean; on click it expands a compact,
 * three-line explainer (What it is / Affects / How to read) drawn from metricExplainers.
 *
 * Two variants:
 *   - "note" (default): a small "How to read" text button — used under a card or section.
 *   - "icon": a bare "?" glyph — used inline next to an individual metric row's label.
 *
 * The root stops click/keydown propagation so the control works inside a click-to-open
 * MetricCard (whose own click opens the dossier) without triggering it.
 */
import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { usePrintMode } from '../print/printMode';
import { getExplainer, MetricExplainer } from '../content/metricExplainers';

interface HowToReadProps {
  /** Key into METRIC_EXPLAINERS. If it resolves to nothing, the component renders null. */
  explainKey: string;
  variant?: 'note' | 'icon';
  /** Optional label override for the "note" variant. */
  label?: string;
  /** Generated explainer (SR-375) — overrides the static copy. */
  content?: MetricExplainer | null;
  className?: string;
}

const Line: React.FC<{ tag: string; children: React.ReactNode }> = ({ tag, children }) => (
  <div className="flex gap-2.5">
    <span className="shrink-0 w-[68px] text-[9px] font-mono uppercase tracking-[0.14em] text-cmd-textDim pt-[2px]">{tag}</span>
    <span className="text-[12px] leading-relaxed text-cmd-textMuted">{children}</span>
  </div>
);

/** The three-line explainer box on its own — for callers that own their open/close state
 *  and need the panel to render full-width in a different place from the trigger. */
export const ExplainerPanel: React.FC<{ explainKey: string; className?: string; content?: MetricExplainer | null }> = ({ explainKey, className = '', content: generated }) => {
  // SR-375: framework-generated copy (pillar_cards[].how_to_read) wins; static copy only for non-pillar metrics.
  const content = generated ?? getExplainer(explainKey);
  if (!content) return null;
  return (
    <div className={`space-y-1.5 rounded-[2px] border border-[color:rgb(var(--c-border-rgb)/0.95)] bg-[color:rgb(var(--c-text-mute-rgb)/0.03)] px-3 py-2.5 ${className}`}>
      <Line tag="What">{content.what}</Line>
      <Line tag="Affects">{content.affects}</Line>
      <Line tag="How to read">{content.read}</Line>
    </div>
  );
};

const HowToRead: React.FC<HowToReadProps> = ({ explainKey, variant = 'note', label = 'How to read', className = '', content: generated }) => {
  const [openState, setOpen] = useState(false);
  const open = usePrintMode() || openState; // printed in full (src/print/printMode.ts)
  const content = generated ?? getExplainer(explainKey);
  if (!content) return null;

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const toggle = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setOpen((o) => !o);
  };

  return (
    <div className={className} onClick={stop} onKeyDown={stop}>
      {variant === 'icon' ? (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-label={`How to read: ${content.what}`}
          className="inline-flex items-center justify-center w-4 h-4 text-cmd-textDim hover:text-cmd-active transition-colors align-middle focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cmd-active rounded-[2px]"
        >
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      ) : (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.12em] text-cmd-textDim hover:text-cmd-active transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cmd-active rounded-[2px] px-0.5"
        >
          <HelpCircle className="w-3.5 h-3.5" />
          {label}
          {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      )}

      {open && <ExplainerPanel explainKey={explainKey} content={content} className="mt-2" />}
    </div>
  );
};

export default HowToRead;
